import { useCallback, useEffect, useRef, useState } from "react";
import { Linking } from "react-native";
import { Camera } from "expo-camera";

import {
  toPermissionState,
  type MediaPermissionState,
  type MediaPermissions,
} from "../mediaPermissions";

/**
 * Reads camera permission state, and asks for the camera.
 *
 * Two things this deliberately does not do.
 *
 * It never prompts on mount. `getCameraPermissionsAsync` reads without
 * surfacing a dialog, so the sheet can tell her truthfully that the camera is
 * blocked in Settings before she taps anything — rather than letting her choose
 * "with camera" and then doing nothing, which is what an unread `blocked` state
 * produces.
 *
 * And it has nothing to do with the microphone. It used to read the recording
 * permission "so the sheet could mention it", and no surface ever did — which
 * left the app declaring a purpose string for a voice feature that does not
 * exist. Voice is how the coach would prefer to be interrupted, but a patient
 * with both hands busy can still tap. When #2276 brings it, the read, the
 * purpose string and a bumped disclosure version arrive together.
 *
 * ### Why `expo-camera` and not `expo-image-picker`
 *
 * They answer differently on the web, and the difference decided when a patient
 * meets the browser's prompt. `ImagePicker.requestCameraPermissionsAsync`
 * returns `granted` on the web without asking anything
 * (`ExponentImagePicker.web.js`), so the disclosure recorded an agreement the
 * OS had never been asked for, and the *real* prompt arrived later — when
 * `getUserMedia` ran, at the injection step, with the pen already in her hand.
 * `consent.ts` names that as the thing not to do: an OS dialog mid-injection is
 * the worst possible moment.
 *
 * `expo-camera`'s own request does a real `getUserMedia` on the web and stops
 * the tracks it opened (`ExpoCameraManager.web.js`), so she is asked where she
 * is deciding, and the answer recorded is the answer she gave. Permission-only
 * here — no camera is opened and no picker is launched. See
 * `no-retention.test.ts`.
 */
export function useCoachMediaPermissions() {
  const [permissions, setPermissions] = useState<MediaPermissions>({
    camera: "undetermined",
  });

  // `canAskAgain` is true both before the first prompt and after a soft refusal,
  // so only our own memory of having asked separates "never asked" from "said
  // no once". A ref rather than state: it must not schedule a render, and it
  // must be readable inside the same callback that sets it.
  const hasAskedCamera = useRef(false);

  const read = useCallback(async () => {
    const camera = await Camera.getCameraPermissionsAsync();
    setPermissions({
      camera: toPermissionState(camera, hasAskedCamera.current),
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    void read().catch(() => {
      // A permission module that cannot answer is not a reason to block her.
      // Leaving the state `undetermined` keeps the no-camera path available,
      // which is the one that always works.
      if (!cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [read]);

  /**
   * Surfaces the OS prompt, once she has asked for the camera.
   *
   * Only ever called from the disclosure sheet's explicit "continue with the
   * camera" choice — never on mount, never from the entry button, and never for
   * a medication with no approved procedure.
   */
  const requestCamera = useCallback(async (): Promise<MediaPermissionState> => {
    hasAskedCamera.current = true;
    try {
      const response = await Camera.requestCameraPermissionsAsync();
      const state = toPermissionState(response, true);
      setPermissions((current) => ({ ...current, camera: state }));
      return state;
    } catch {
      // Treated as a refusal, which costs her nothing: the session continues
      // without the camera either way.
      setPermissions((current) => ({ ...current, camera: "denied" }));
      return "denied";
    }
  }, []);

  /** For `blocked`, where only Settings can change the answer. */
  const openSystemSettings = useCallback(() => {
    void Linking.openSettings();
  }, []);

  return { permissions, refresh: read, requestCamera, openSystemSettings };
}
